import { MatDialog } from '@angular/material/dialog';
import { AppdataService } from '../../service/appdata.service';
import { CompaniesService } from './../../service/companies.service';
import { Component, inject, OnInit, DestroyRef } from '@angular/core';
import { AddCompanyComponent } from './add-company/add-company.component';
import { ShowUserComponent } from './show-user/show-user.component';
import { Company, CompanyType } from '../../lib/interfaces/company';
import { PageEvent } from '@angular/material/paginator';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, from, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-companies',
    standalone: false,
    templateUrl: './companies.component.html',
    styleUrl: './companies.component.scss'
})
export class CompaniesComponent implements OnInit {
    // paginator
    protected length = 50;
    protected pageSize = 50;
    protected pageIndex = 0;
    protected pageSizeOptions = [50, 75, 100];

    // Injections
    compService = inject(CompaniesService);
    appData = inject(AppdataService);
    dialog = inject(MatDialog);

    // Objects
    companiesList: Company[] = [];
    companyTypes: CompanyType[] = [];

    // Variables
    // Filter
    searchQuery = new FormControl('');
    selectedTypeFilter: string[] = []

    private destroyRef = inject(DestroyRef);

    async ngOnInit(): Promise<void> {
        this.loadAllCompanyiesWithFilter(this.pageIndex, this.pageSize);
        this.appData.companyTypes.subscribe(d => {
            if (d) { this.companyTypes = d; }
        });
        this.onSearchChange();
    }

    protected async handlePageEvent(e: PageEvent): Promise<void> {
        this.pageSize = e.pageSize;
        this.pageIndex = e.pageIndex;

        await this.loadAllCompanyiesWithFilter(this.pageIndex * this.pageSize, this.pageSize, this.searchQuery.value ?? undefined);
    }

    matchCompanyNameToTypes() {
        this.companiesList.forEach(company => {
            let r = this.companyTypes.find(type => type.id === company.companyTypeId);
            if (r) company.companyTypeName = r.name;
            else company.companyTypeName = 'Unknown';
        });
    }

    async onFilterTypeChange(type: string, checked: boolean) {
        if (checked === false) {
            this.selectedTypeFilter = this.selectedTypeFilter.filter(c => c !== type);
        } else {
            this.selectedTypeFilter.push(type);
        }

        await this.loadAllCompanyiesWithFilter(this.pageIndex, this.pageSize, this.searchQuery.value ?? undefined);
    }
    async onSearchChange(): Promise<void> {
        this.pageIndex = 0;

        this.searchQuery.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                switchMap((value) => {
                    const searchString = value?.length ? value : undefined;

                    this.pageIndex = 0;

                    return from(this.loadAllCompanyiesWithFilter(this.pageIndex, this.pageSize, searchString));
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

    }
    async loadAllCompanyiesWithFilter(offset: number, limit: number, search?: string) {
        const filters = this.selectedTypeFilter;

        let list = await this.compService.getHighestLevelCompanies(offset, limit, search, filters);
        if (list === false) return;
        this.companiesList = [...list.items];
        this.length = list.totalCount;
        this.matchCompanyNameToTypes();
    }

    async addSubCompany(parentCompany: Company) {
        let dia = this.dialog.open(AddCompanyComponent, {
            panelClass: '',
            autoFocus: false,
            data: { parentId: parentCompany.companyId }
        });

        dia.afterClosed().subscribe(async result => {
            if (result) {
                await this.loadAllCompanyiesWithFilter(this.pageIndex, this.pageSize, this.searchQuery.value ?? undefined);

                return;
            }

            this.updateRefreshToken(parentCompany);
        });
    }
    addNewCompany(parentCompany: Company | undefined = undefined) {
        let dia = this.dialog.open(AddCompanyComponent, {
            panelClass: 'company-dialog',
            autoFocus: false,
            data: { ...parentCompany }
        });

        dia.afterClosed().subscribe(async result => {
            if (result) {
                await this.loadAllCompanyiesWithFilter(this.pageIndex, this.pageSize, this.searchQuery.value ?? undefined);

                return;
            }

            this.updateRefreshToken(parentCompany);
        });
    }

    showUsers(company: Company) {
        this.dialog.open(ShowUserComponent, {
            data: company,
            autoFocus: false,
            panelClass: 'wide-dialog',
        });
    }

    private updateRefreshToken(parentCompany?: Company): void {
        if (parentCompany?.companyId) {
            const company = this.companiesList.find((c) => c.companyId === parentCompany.companyId);

            if (company) {
                company.refreshToken = new Date().getTime();
            }
        }
    }
}

